// Renders an assistant answer with a per-message Insights | Text Analysis toggle.
// Both representations are always available (the markdown text is the source the
// visual is built from); this just lets the user switch which one they see. When
// there is no visual, it renders the text directly with no toggle.
// (The internal view keys stay 'visual'/'markdown'; only the user-facing labels are
// "Insights"/"Text Analysis".)

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InsightDocument from './InsightDocument';
import HtmlVisual from './HtmlVisual';
import { markdownComponents } from './markdown';
import { hasRenderableVisual, isHtmlVisual } from './util';
import type { InsightDocument as Doc, VisualPayload } from './types';

type View = 'visual' | 'markdown';

const Markdown: React.FC<{ content: string }> = ({ content }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
);

// `tour` gives each tab a stable data-tour hook the guided tour can both target and
// click — it selects a tab during the matching step so the tab shows as active.
const TABS: { key: View; label: string; tour: string }[] = [
  { key: 'visual', label: 'Insights', tour: 'insights-tab' },
  { key: 'markdown', label: 'Text Analysis', tour: 'text-analysis-tab' },
];

export const InsightToggleView: React.FC<{ content: string; visual?: VisualPayload | null }> = ({ content, visual }) => {
  const [view, setView] = useState<View>('visual');

  if (!hasRenderableVisual(visual)) {
    return <Markdown content={content} />;
  }

  // Inline (not a nested component) so the iframe/InsightDocument subtree keeps its
  // identity across re-renders instead of remounting (which would reload the iframe).
  const visualEl = isHtmlVisual(visual!)
    ? <HtmlVisual html={visual.html} />
    : <InsightDocument doc={visual as Doc} />;

  return (
    <div data-tour="insights-answer">
      <div
        role="tablist"
        aria-label="Answer format"
        data-tour="insights-toggle"
        className="inline-flex items-center gap-0.5 mb-3 p-0.5 rounded-lg bg-[#FAFBFC] border border-[#E5E7EB]"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            data-tour={t.tour}
            aria-selected={view === t.key}
            onClick={() => setView(t.key)}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              view === t.key
                ? 'bg-white text-[#1C1C1E] shadow-sm'
                : 'text-[#9CA3AF] hover:text-[#374151]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div>{view === 'visual' ? visualEl : <Markdown content={content} />}</div>
    </div>
  );
};

export default InsightToggleView;
